/* =============================================================================
 * Zenyth Group   |   Accessibility Remediation Layer  (Behavior)
 * -----------------------------------------------------------------------------
 * Store          : Essential Elements
 * Standard       : WCAG 2.2 Level AA
 * File           : zenyth_scripts.js
 * Load           : deferred, linked last in the document head, so it enhances
 *                  the existing markup after parse without blocking render and
 *                  without altering theme behavior (add-only, progressive).
 * Maintained by  : Zenyth Group   .   https://zenythgroup.com
 * Contact        : accessibility@zenythgroup.com
 * -----------------------------------------------------------------------------
 * PURPOSE
 *   Behavioral accessibility corrections applied as progressive enhancement:
 *   keyboard operability, name/role/state wiring (for example aria-expanded
 *   on disclosure triggers), and guards for single-character key shortcuts.
 *
 * TRACEABILITY CONVENTION
 *   Every correction is wrapped in a numbered marker so it can be reviewed
 *   and reverted in isolation:
 *
 *       open   ->  Fix by Zenyth - #0001
 *       close  ->  END OF - Fix by Zenyth - #0001
 *
 *   A single correction that resolves more than one issue lists every number
 *   on the marker (for example: #0007, #0012). The human-readable detail for
 *   each number is kept in Zenyth's internal remediation log.
 * =============================================================================
 */
(function () {
  "use strict";

  /* Corrections are registered below as remediation proceeds. */

  /* Fix by Zenyth - ZA 42899 (Safari keyboard stops for links and buttons, 2.1.1) */
  /* Related: assets/zenyth_scripts.js. Safari does not make links or buttons Tab stops unless "Press Tab to
     highlight each item" is on (or Option+Tab is used). The UserWay overlay used to add a workaround for this
     and the stores are removing it, so this keeps keyboard access in Safari without it. An explicit
     tabindex="0" makes an element a Tab stop in Safari and changes nothing in other browsers, so it is added
     only in Safari, only to links and enabled buttons that have no tabindex of their own. No visual change. */
  (function () {
    var ua = navigator.userAgent || "";
    var safari = ua.indexOf("Safari/") > -1 && !/(Chrome|Chromium|CriOS|FxiOS|EdgiOS|Edg|OPR|OPiOS|Android)/.test(ua);
    if (!safari) return;

    var SELECTOR = "a[href]:not([tabindex]), button:not([disabled]):not([tabindex])";

    function mark(root) {
      if (!root || root.nodeType !== 1) return;
      var list = [];
      if (root.matches && root.matches(SELECTOR)) list.push(root);
      var inner = root.querySelectorAll ? root.querySelectorAll(SELECTOR) : [];
      for (var i = 0; i < inner.length; i++) list.push(inner[i]);
      for (var j = 0; j < list.length; j++) {
        list[j].setAttribute("tabindex", "0");
        list[j].setAttribute("data-znt-safari-tab", "");
      }
    }

    function start() {
      mark(document.documentElement);
      var pending = false;
      var queue = [];
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (m.type === "attributes") queue.push(m.target);
          else for (var j = 0; j < m.addedNodes.length; j++) queue.push(m.addedNodes[j]);
        }
        if (pending) return;
        pending = true;
        window.setTimeout(function () {
          var items = queue;
          queue = [];
          pending = false;
          for (var k = 0; k < items.length; k++) mark(items[k]);
        }, 100);
      }).observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["tabindex", "href", "disabled"]
      });
    }

    if (document.readyState !== "loading") start();
    else document.addEventListener("DOMContentLoaded", start);
  })();
  /* END OF - Fix by Zenyth - ZA 42899 (Safari keyboard stops for links and buttons, 2.1.1) */

  /* Fix by Zenyth - ZA 42190 (focus ring on text fields only for keyboard users, 2.4.7) */
  /* Text fields always match :focus-visible, even after a mouse click. This marks the page as "pointer" after a mouse or touch press and
     clears it on a keyboard move (Tab, or Enter/Space/arrows outside a text field), so the ring shows for keyboard users only. If this
     script does not run, the ring simply always shows. */
  (function () {
    var root = document.documentElement;
    function isText(el) {
      return el && el.matches && el.matches("textarea, input:not([type=button]):not([type=submit]):not([type=reset]):not([type=checkbox]):not([type=radio]):not([type=image]):not([type=file]):not([type=range]):not([type=color])");
    }
    document.addEventListener("pointerdown", function () { root.classList.add("znt-pointer"); }, true);
    document.addEventListener("keydown", function (e) {
      var k = e.key;
      if (k === "Tab" || (/^(Enter| |Escape|ArrowUp|ArrowDown|ArrowLeft|ArrowRight)$/.test(k) && !isText(e.target))) {
        root.classList.remove("znt-pointer");
      }
    }, true);
  })();
  /* END OF - Fix by Zenyth - ZA 42190 (focus ring on text fields only for keyboard users, 2.4.7) */

  /* Fix by Zenyth - ZA 42276 (EE predictive-search dialog a11y, 2.4.3 / 2.1.2 / 4.1.2) */
  (function () {
    var CONTAINER = ".site-header__search-container";
    var TRIGGER = ".js-search-header";
    var lastTrigger = null;
    var trapEl = null;

    document.addEventListener("click", function (e) {
      var t = e.target && e.target.closest ? e.target.closest(TRIGGER) : null;
      if (t) lastTrigger = t;
    }, true);

    function focusables(el) {
      return Array.prototype.slice
        .call(el.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]'))
        .filter(function (n) {
          if (n.getAttribute("tabindex") === "-1") return false;
          return n.offsetWidth > 0 || n.offsetHeight > 0 || n === document.activeElement;
        });
    }

    function onKeydown(e) {
      if (e.key !== "Tab" || !trapEl) return;
      var f = focusables(trapEl);
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function openTrap() {
      trapEl = document.querySelector(CONTAINER + ".is-active") || document.querySelector(CONTAINER);
      if (!trapEl) return;
      trapEl.setAttribute("role", "dialog");
      trapEl.setAttribute("aria-modal", "true");
      if (!trapEl.getAttribute("aria-label")) trapEl.setAttribute("aria-label", "Search");
      trapEl.addEventListener("keydown", onKeydown);
    }

    function closeTrap() {
      if (trapEl) {
        trapEl.removeEventListener("keydown", onKeydown);
        trapEl.removeAttribute("aria-modal");
        trapEl = null;
      }
      if (lastTrigger) setTimeout(function () { try { lastTrigger.focus(); } catch (e) {} }, 0);
    }

    document.addEventListener("predictive-search:open", function () { setTimeout(openTrap, 0); });
    document.addEventListener("predictive-search:close-all", closeTrap);
    document.addEventListener("predictive-search:close", closeTrap);
  })();
  /* END OF - Fix by Zenyth - ZA 42276 (EE predictive-search dialog a11y) */

  /* Fix by Zenyth - ZA 42305, 42288 (cart icon keyboard, 2.1.1 / 4.1.2) */
  (function () {
    function enhance(el) {
      if (!el || el.dataset.zntCartKbd || el.hasAttribute("href")) return;
      el.dataset.zntCartKbd = "1";
      if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "0");
      if (!el.hasAttribute("role")) el.setAttribute("role", "link");
      el.addEventListener("keydown", function (e) {
        if (e.key === "Enter") { e.preventDefault(); el.click(); }
      });
    }
    function run() {
      ["#HeaderCartTrigger", "#cart-icon-bubble"].forEach(function (sel) {
        Array.prototype.forEach.call(document.querySelectorAll(sel), enhance);
      });
    }
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
    else run();
  })();
  /* END OF - Fix by Zenyth - ZA 42305, 42288 (cart icon keyboard, 2.1.1 / 4.1.2) */

  /* Fix by Zenyth - ZA 42314 (PDP gallery a11y, 2.1.1 / 2.4.3 / 4.1.2 / 4.1.3) */
  (function () {
    function announceInto(el, msg) { el.textContent = ''; setTimeout(function () { el.textContent = msg; }, 60); }
    function centerVisible(el, vp) {
      if (!el || !vp) return true;
      var r = el.getBoundingClientRect(), v = vp.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return false;
      var cx = r.left + r.width / 2;
      return cx >= v.left - 1 && cx <= v.right + 1;
    }

    function enhance(main) {
      if (main.dataset.zntGallery) return;
      main.dataset.zntGallery = '1';
      var root = main.closest('.section-product__slider') || main.parentElement || document;

      main.setAttribute('role', 'group');
      main.setAttribute('aria-roledescription', 'carousel');
      if (!main.getAttribute('aria-label')) main.setAttribute('aria-label', 'Product gallery');

      var live = document.createElement('div');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      live.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
      main.appendChild(live);

      function realSlides() {
        return Array.prototype.filter.call(main.querySelectorAll('.glide__slide'), function (s) {
          return s.className.indexOf('glide__slide--clone') === -1;
        });
      }
      // main slides: only the active one is exposed to AT
      function syncMainSlides() {
        Array.prototype.forEach.call(main.querySelectorAll('.glide__slide'), function (s) {
          s.setAttribute('aria-hidden', s.className.indexOf('glide__slide--active') > -1 ? 'false' : 'true');
        });
      }
      function announce() {
        var s = realSlides();
        var a = main.querySelector('.glide__slide--active');
        var i = s.indexOf(a);
        if (i >= 0) announceInto(live, 'Image ' + (i + 1) + ' of ' + s.length);
      }
      syncMainSlides();
      var mainTrack = main.querySelector('.glide__slides') || main;
      new MutationObserver(function () { syncMainSlides(); announce(); })
        .observe(mainTrack, { attributes: true, attributeFilter: ['class'], subtree: true });

      var thumbsWrap = root.querySelector('.js--section-product__thumbnails');
      if (thumbsWrap) {
        var viewport = thumbsWrap.querySelector('.glide__track') || thumbsWrap;
        var total = realSlides().length || Array.prototype.filter.call(thumbsWrap.querySelectorAll('.section-product__thumb-item'), function (t) { return !t.closest('.glide__slide--clone'); }).length;

        function enhanceThumb(t) {
          if (t.dataset.zntThumb) return;
          t.dataset.zntThumb = '1';
          var idx = parseInt(t.getAttribute('data-img-number'), 10);
          var n = isNaN(idx) ? '' : (idx + 1);
          t.setAttribute('role', 'button');
          t.setAttribute('aria-label', n ? ('Show image ' + n + ' of ' + total) : 'Show image');
          t.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); t.click(); }
          });
        }
        // Keep only the visible thumbnails focusable + in the AT tree
        function updateThumbs() {
          Array.prototype.forEach.call(thumbsWrap.querySelectorAll('.section-product__thumb-item'), function (t) {
            enhanceThumb(t);
            var li = t.closest('.glide__slide') || t;
            var vis = centerVisible(li, viewport);
            if (vis) {
              t.setAttribute('tabindex', '0');
              t.removeAttribute('aria-hidden');
              li.removeAttribute('aria-hidden');
            } else {
              t.setAttribute('tabindex', '-1');
              t.setAttribute('aria-hidden', 'true');
              li.setAttribute('aria-hidden', 'true');
            }
            t.setAttribute('aria-current', t.className.indexOf('section-product__thumb-item--active') > -1 ? 'true' : 'false');
          });
        }
        updateThumbs();
        var thumbTrack = thumbsWrap.querySelector('.glide__slides') || thumbsWrap;
        new MutationObserver(updateThumbs).observe(thumbTrack, { attributes: true, attributeFilter: ['style', 'class'], subtree: true });
        window.addEventListener('resize', function () { setTimeout(updateThumbs, 150); });

        // Make the ACTUAL arrow element the button. That element is what the theme both positions and
        // binds the click to: the wrapper div on the left, but the inner <i> on the right. Putting the
        // role/tabindex/focus on the wrapper div displaced the right control and misplaced its focus ring.
        Array.prototype.forEach.call(thumbsWrap.querySelectorAll('.section-product__slider-arrow-left, .section-product__slider-arrow-right'), function (arrow) {
          if (arrow.dataset.zntCtrl) return;
          arrow.dataset.zntCtrl = '1';
          var isPrev = arrow.className.indexOf('arrow-left') > -1 || arrow.className.indexOf('fa-chevron-left') > -1 || !!arrow.querySelector('.fa-chevron-left');
          arrow.setAttribute('role', 'button');
          arrow.setAttribute('tabindex', '0');
          arrow.setAttribute('aria-label', isPrev ? 'Previous images' : 'Next images');
          arrow.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); arrow.click(); }
          });
        });
      }
      announce();
    }

    function run() { Array.prototype.forEach.call(document.querySelectorAll('.js--section-product__slider'), enhance); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(run, 500); });
    else setTimeout(run, 500);
  })();
  /* END OF - Fix by Zenyth - ZA 42314 (PDP gallery a11y, 2.1.1 / 2.4.3 / 4.1.2 / 4.1.3) */

  /* Fix by Zenyth - ZA 42191 (nav top-level disclosure, 2.1.1 / 4.1.2) */
  /* Related: snippets/tailwind-header-desktop-nav-featured.liquid, assets/zenyth_styles.css */
  (function () {
    function panelFor(btn) { return document.getElementById(btn.getAttribute("aria-controls")); }
    function setOpen(btn, panel, open) {
      btn.setAttribute("aria-expanded", open ? "true" : "false");
      if (panel) { if (open) panel.removeAttribute("hidden"); else panel.setAttribute("hidden", ""); }
    }
    function closeAll(except) {
      Array.prototype.forEach.call(document.querySelectorAll('.znt-mega-toggle[aria-expanded="true"]'), function (b) {
        if (b === except) return;
        setOpen(b, panelFor(b), false);
      });
    }
    function enhance(btn) {
      if (btn.dataset.zntMegaTop) return;
      var panel = panelFor(btn);
      if (!panel) return;
      btn.dataset.zntMegaTop = "1";
      var item = btn.closest(".site-nav__item");
      // Mouse: hovering the item opens the panel and leaving closes it, as the original
      // menu did. A mouse click on an already open panel does not close it.
      var hoverOpened = false, leaveTimer = 0, lastPointer = "";
      btn.addEventListener("pointerdown", function (e) { lastPointer = e.pointerType || ""; });
      if (item) {
        item.addEventListener("pointerenter", function (e) {
          if (e.pointerType !== "mouse") return;
          window.clearTimeout(leaveTimer);
          if (btn.getAttribute("aria-expanded") !== "true") {
            closeAll(btn);
            setOpen(btn, panel, true);
            hoverOpened = true;
          }
        });
        item.addEventListener("pointerleave", function (e) {
          if (e.pointerType !== "mouse") return;
          window.clearTimeout(leaveTimer);
          leaveTimer = window.setTimeout(function () {
            if (hoverOpened) { hoverOpened = false; setOpen(btn, panel, false); }
          }, 150);
        });
      }
      btn.addEventListener("click", function () {
        var viaMouse = lastPointer === "mouse";
        lastPointer = "";
        var open = btn.getAttribute("aria-expanded") !== "true";
        if (!open && viaMouse) return;
        hoverOpened = false;
        if (open) closeAll(btn);
        setOpen(btn, panel, open);
      });
      function onEsc(e) {
        if (e.key === "Escape" && btn.getAttribute("aria-expanded") === "true") {
          setOpen(btn, panel, false);
          btn.focus();
        }
      }
      btn.addEventListener("keydown", onEsc);
      panel.addEventListener("keydown", onEsc);
      if (item) {
        item.addEventListener("focusout", function () {
          setTimeout(function () {
            if (!item.contains(document.activeElement)) setOpen(btn, panel, false);
          }, 0);
        });
      }
    }
    function run() { Array.prototype.forEach.call(document.querySelectorAll(".znt-mega-toggle"), enhance); }
    document.addEventListener("click", function (e) {
      if (!e.target.closest(".site-nav__item.znt-mega")) closeAll(null);
    });
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
    else run();
  })();
  /* END OF - Fix by Zenyth - ZA 42191 (nav top-level disclosure, 2.1.1 / 4.1.2) */

  /* Fix by Zenyth - ZA 42191 (mega tablist, 2.1.1 / 3.2.1 / 4.1.2) */
  /* Related: snippets/tailwind-header-desktop-nav-featured.liquid, assets/zenyth_styles.css */
  (function () {
    function enhance(list) {
      if (list.dataset.zntTablist) return;
      list.dataset.zntTablist = "1";
      var tabs = Array.prototype.filter.call(list.querySelectorAll('[role="tab"]'), function (t) {
        return !!document.getElementById(t.getAttribute("aria-controls"));
      });
      if (!tabs.length) return;
      var panels = tabs.map(function (t) { return document.getElementById(t.getAttribute("aria-controls")); });
      function activate(idx, moveFocus) {
        tabs.forEach(function (tab, i) {
          var on = i === idx, panel = panels[i];
          tab.setAttribute("aria-selected", on ? "true" : "false");
          tab.setAttribute("tabindex", on ? "0" : "-1");
          if (on) tab.classList.add("megamenu__col-title-hovered");
          else tab.classList.remove("megamenu__col-title-hovered");
          if (panel) {
            panel.setAttribute("aria-hidden", on ? "false" : "true");
            panel.style.display = on ? "flex" : "none";
          }
        });
        if (moveFocus && tabs[idx]) tabs[idx].focus();
      }
      tabs.forEach(function (tab, idx) {
        tab.addEventListener("click", function () { activate(idx); });
        tab.addEventListener("mouseenter", function () { activate(idx); });
        tab.addEventListener("keydown", function (e) {
          var last = tabs.length - 1, next;
          if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") { e.preventDefault(); activate(idx, true); return; }
          if (e.key === "ArrowDown" || e.key === "ArrowRight") next = idx === last ? 0 : idx + 1;
          else if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = idx === 0 ? last : idx - 1;
          else if (e.key === "Home") next = 0;
          else if (e.key === "End") next = last;
          else return;
          e.preventDefault();
          activate(next, true);
        });
      });
      var initial = 0;
      tabs.forEach(function (t, i) { if (t.getAttribute("aria-selected") === "true") initial = i; });
      activate(initial);
    }
    function run() { Array.prototype.forEach.call(document.querySelectorAll('.megamenu [role="tablist"]'), enhance); }
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
    else run();
  })();
  /* END OF - Fix by Zenyth - ZA 42191 (mega tablist, 2.1.1 / 3.2.1 / 4.1.2) */

  /* Fix by Zenyth - ZA 42193 (PDP content tabs a11y, 2.1.1 / 2.4.3 / 4.1.2) */
  /* Related: sections/tailwind-product-tabs.liquid, assets/zenyth_styles.css */
  (function () {
    var NAV = ".section-content__tab-navigation";
    var PANEL_SELECTORS = ".section-content__science, .section-content__description, .section-content__ingredients, .section-reviews";
    var uid = 0;

    function neutralizeAnchors() {
      Array.prototype.forEach.call(document.querySelectorAll("a.section[name]"), function (a) {
        if (a.hasAttribute("tabindex")) a.setAttribute("tabindex", "-1");
        if (a.getAttribute("aria-label") === "Open this option") a.removeAttribute("aria-label");
        a.removeAttribute("data-uw-rm-empty-ctrl");
        a.removeAttribute("data-uw-rm-kbnav");
      });
    }

    function panelsFor(tab) {
      var sel = tab.getAttribute("data-target-content");
      return sel ? Array.prototype.slice.call(document.querySelectorAll(sel)) : [];
    }

    function isDesktop(nav) { return getComputedStyle(nav).display !== "none"; }

    function syncSelected(nav, tabs) {
      if (!isDesktop(nav)) return;
      tabs.forEach(function (tab) {
        var on = tab.classList.contains("active");
        tab.setAttribute("aria-selected", on ? "true" : "false");
        tab.setAttribute("tabindex", on ? "0" : "-1");
      });
    }

    function strip(nav, tabs) {
      nav.removeAttribute("role");
      nav.removeAttribute("aria-label");
      tabs.forEach(function (t) {
        ["role", "aria-selected", "aria-controls", "tabindex"].forEach(function (a) { t.removeAttribute(a); });
      });
      Array.prototype.forEach.call(document.querySelectorAll(PANEL_SELECTORS), function (p) {
        ["role", "aria-labelledby", "tabindex"].forEach(function (a) { p.removeAttribute(a); });
      });
    }

    function bindKeys(nav, tabs) {
      if (nav.dataset.zntPdpKeys) return;
      nav.dataset.zntPdpKeys = "1";
      tabs.forEach(function (tab, idx) {
        tab.addEventListener("keydown", function (e) {
          var last = tabs.length - 1, next;
          if (e.key === "ArrowRight" || e.key === "ArrowDown") next = idx === last ? 0 : idx + 1;
          else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = idx === 0 ? last : idx - 1;
          else if (e.key === "Home") next = 0;
          else if (e.key === "End") next = last;
          else return;
          e.preventDefault();
          tabs[next].focus();
          tabs[next].click(); // theme.js toggles .active; the observer re-syncs aria-selected
        });
      });
    }

    function observe(nav, tabs) {
      if (nav.dataset.zntPdpObs) return;
      nav.dataset.zntPdpObs = "1";
      var mo = new MutationObserver(function () { syncSelected(nav, tabs); });
      tabs.forEach(function (t) { mo.observe(t, { attributes: true, attributeFilter: ["class"] }); });
    }

    function apply(nav) {
      var tabs = Array.prototype.slice.call(nav.querySelectorAll(".tab-header"));
      if (!tabs.length) return;
      if (!isDesktop(nav)) { strip(nav, tabs); return; }

      nav.setAttribute("role", "tablist");
      if (!nav.getAttribute("aria-label")) nav.setAttribute("aria-label", "Product information");

      tabs.forEach(function (tab) {
        if (!tab.id) tab.id = "znt-pdp-tab-" + (++uid);
        var panels = panelsFor(tab);
        var ids = [];
        panels.forEach(function (p) {
          if (!p.id) p.id = "znt-pdp-panel-" + (++uid);
          ids.push(p.id);
          p.setAttribute("role", "tabpanel");
          if (!p.getAttribute("aria-labelledby")) p.setAttribute("aria-labelledby", tab.id);
          if (!p.hasAttribute("tabindex")) p.setAttribute("tabindex", "0");
        });
        tab.setAttribute("role", "tab");
        if (ids.length) tab.setAttribute("aria-controls", ids.join(" "));
        var on = tab.classList.contains("active");
        tab.setAttribute("aria-selected", on ? "true" : "false");
        tab.setAttribute("tabindex", on ? "0" : "-1");
      });

      bindKeys(nav, tabs);
      observe(nav, tabs);
    }

    function run() {
      neutralizeAnchors();
      Array.prototype.forEach.call(document.querySelectorAll(NAV), apply);
    }

    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
    else run();
    var rt;
    window.addEventListener("resize", function () { clearTimeout(rt); rt = setTimeout(run, 200); });
  })();
  /* END OF - Fix by Zenyth - ZA 42193 (PDP content tabs a11y, 2.1.1 / 2.4.3 / 4.1.2) */

  /* Fix by Zenyth - ZA 42332 (Subscribe & Save popup focus return, 2.4.3) */
  /* Related: sections/subscribe-and-save-popup.liquid. On close (Esc, X or "Got it") focus must go back to the
     trigger. The theme tries to, but its Modal.close chains .focus() onto setAttribute(), which returns undefined,
     so the call throws and focus is left on the body. Add-only: we restore focus after the theme close transition. */
  (function () {
    var TRIGGER = ".open-subscribe-and-save-modal";
    var lastTrigger = null;
    document.addEventListener("click", function (e) {
      var t = e.target && e.target.closest ? e.target.closest(TRIGGER) : null;
      if (t) lastTrigger = t;
    }, true);
    function visible(el) { return el && (el.offsetWidth > 0 || el.offsetHeight > 0); }
    function watch(modal) {
      var wasOpen = modal.classList.contains("modal--is-active");
      new MutationObserver(function () {
        var isOpen = modal.classList.contains("modal--is-active");
        if (wasOpen && !isOpen) {
          setTimeout(function () {
            var target = visible(lastTrigger) ? lastTrigger
              : Array.prototype.filter.call(document.querySelectorAll(TRIGGER), visible)[0];
            if (target) { try { target.focus(); } catch (e) {} }
          }, 550); /* after the theme 500ms close transition */
        }
        wasOpen = isOpen;
      }).observe(modal, { attributes: true, attributeFilter: ["class"] });
    }
    function run() {
      Array.prototype.forEach.call(document.querySelectorAll('[id^="SubscribeAndSavePopup"]'), watch);
    }
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
    else run();
  })();
  /* END OF - Fix by Zenyth - ZA 42332 (Subscribe & Save popup focus return, 2.4.3) */
  /* Fix by Zenyth - ZA 42333, 42334, 42335 (shop categories carousel a11y, 2.1.1 / 2.4.3 / 4.1.2) */
  /* Related: sections/shop-categories-carousel.liquid, assets/zenyth_styles.css. Glide renders clone slides and
     off-screen slides whose links stay in the tab order and the accessibility tree. Only the slides actually inside
     the track stay reachable; clones and off-screen slides get aria-hidden + tabindex=-1. Re-evaluated whenever Glide
     moves or re-renders the track, and on resize. The arrows are native buttons in the section markup. */
  (function () {
    /* Judged from the Glide TARGET position (layout offset + the inline translate), not the painted rect, so the
       result is the settled state even while the 400ms transition is still running. */
    function shiftX(list) {
      var m = /translate3d\((-?[\d.]+)px/.exec(list.style.transform || "");
      return m ? parseFloat(m[1]) : 0;
    }
    function inView(s, list, track) {
      if (!s.offsetWidth) return false;
      /* the transformed list is the slides' offsetParent, so offsetLeft is already list-relative */
      var left = s.offsetParent === list ? s.offsetLeft : s.offsetLeft - list.offsetLeft;
      var cx = left + s.offsetWidth / 2 + shiftX(list);
      return cx >= -1 && cx <= track.clientWidth + 1;
    }
    function enhance(glide) {
      if (glide.dataset.zntCats) return;
      var track = glide.querySelector(".glide__track");
      var list = glide.querySelector(".glide__slides");
      if (!track || !list) return;
      glide.dataset.zntCats = "1";
      glide.setAttribute("role", "group");
      glide.setAttribute("aria-roledescription", "carousel");
      if (!glide.getAttribute("aria-label")) glide.setAttribute("aria-label", "Shop categories");
      var t;
      function sync() {
        Array.prototype.forEach.call(list.querySelectorAll(".glide__slide"), function (s) {
          var show = s.className.indexOf("glide__slide--clone") === -1 && inView(s, list, track);
          if (show) s.removeAttribute("aria-hidden"); else s.setAttribute("aria-hidden", "true");
          Array.prototype.forEach.call(s.querySelectorAll("a, button"), function (a) {
            if (show) a.removeAttribute("tabindex"); else a.setAttribute("tabindex", "-1");
          });
        });
      }
      function later() { clearTimeout(t); t = setTimeout(sync, 450); } /* after the 400ms Glide transition */
      new MutationObserver(later).observe(list, { attributes: true, attributeFilter: ["style"], childList: true });
      window.addEventListener("resize", later);
      sync();
    }
    function run() {
      Array.prototype.forEach.call(document.querySelectorAll(".shop-categories-carousel .glide"), enhance);
    }
    if (document.readyState === "complete") run();
    else window.addEventListener("load", function () { setTimeout(run, 50); }); /* the section mounts Glide on load */
  })();
  /* END OF - Fix by Zenyth - ZA 42333, 42334, 42335 (shop categories carousel a11y, 2.1.1 / 2.4.3 / 4.1.2) */

  /* Fix by Zenyth - ZA 42306 (mobile nav button aria-expanded, 4.1.2) */
  (function () {
    /* The theme (theme.MobileNav) adds/removes the "is-active" class on every .mobile-nav-trigger when the drawer
       opens/closes, so that class is the real open state. Mirror it into aria-expanded. */
    function sync(btn) {
      btn.setAttribute("aria-expanded", btn.classList.contains("is-active") ? "true" : "false");
    }
    function enhance(btn) {
      if (btn.dataset.zntNavExpanded) return;
      btn.dataset.zntNavExpanded = "1";
      sync(btn);
      new MutationObserver(function () { sync(btn); }).observe(btn, { attributes: true, attributeFilter: ["class"] });
    }
    function run() {
      Array.prototype.forEach.call(document.querySelectorAll(".mobile-nav-trigger"), enhance);
    }
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
    else run();
    document.addEventListener("shopify:section:load", run); /* theme editor re-renders the header */
  })();
  /* END OF - Fix by Zenyth - ZA 42306 (mobile nav button aria-expanded, 4.1.2) */

  /* Fix by Zenyth - ZA 42323 (Filter Products opener now role=button, Space key support, 4.1.2) */
  (function () {
    function run() {
      var b = document.getElementById("filter-btn");
      if (!b || b.dataset.zntFilterBtn) return;
      b.dataset.zntFilterBtn = "1";
      b.addEventListener("keydown", function (e) {
        if (e.key === " " || e.key === "Spacebar") { e.preventDefault(); b.click(); }
      });
    }
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
    else run();
  })();
  /* END OF - Fix by Zenyth - ZA 42323 (Filter Products opener Space key, 4.1.2) */
})();
